import Text from "common/Typography/Text";
import CustomInputNumber from "common/Inputs/CustomInputNumber";
import Slider from "common/Slider/Slider";
import styled, { css, useTheme } from "styled-components";
import { typography } from "@shared/common/Themes/typography";
import useConnectedAmountPercentInputs, {
  useConnectedAmountPercentInputsProps,
} from "@shared/common/Inputs/useConnectedAmountPercentInputs";
import { ReactNode, useContext } from "react";
import { DrawerContext } from "common/Drawers/Drawer.context.ts";
import { useBottomNavigator } from "utils/hooks/misc.tsx";
import { newLookFieldStates } from "common/Inputs/styles";

interface ConnectedAmountPercentInputsProps
  extends useConnectedAmountPercentInputsProps {
  bordered?: boolean;
  icon?: ReactNode;
  showSlider?: boolean;
  validationMessage?: ReactNode;
  focusOnMount?: boolean;
}

const ConnectedAmountPercentInputs = ({
  amountPrecision,
  percentPrecision,
  availableAmount,
  amount,
  onAmountChange,
  percent,
  onPercentChange,
  maxPercent,
  defaultPercent = undefined,
  onChange = undefined,
  icon,
  showSlider,
  useDebounce,
  bordered = false,
  validationMessage,
  focusOnMount = true,
}: ConnectedAmountPercentInputsProps) => {
  const { isDrawer } = useContext(DrawerContext) || {};
  const { isNewLook } = useTheme();
  // New look: the bordered variant is one field (amount | percent) whose
  // parts share a single border, hover and focus ring.
  const isNewLookField = isNewLook && bordered;
  const { baseShouldDisplay: isBottomNavigator } = useBottomNavigator();
  const { amountInputData, percentInputData, amountFocused } =
    useConnectedAmountPercentInputs({
      amountPrecision,
      percentPrecision,
      availableAmount,
      amount,
      onAmountChange,
      percent,
      maxPercent,
      defaultPercent,
      useDebounce,
      onPercentChange,
      onChange,
    });
  const focusedProps = bordered
    ? {}
    : {
        $size: isBottomNavigator ? "28px" : typography.fontSizes.XXL,
        $weight: typography.fontWeights.SEMI_BOLD,
      };
  const unfocusedProps = bordered
    ? {}
    : {
        $size: isBottomNavigator ? "18px" : typography.fontSizes.XL,
        $weight: typography.fontWeights.SEMI_BOLD,
        type: "secondary",
      };

  return (
    <div style={{ width: "100%" }}>
      <InputsWrapper
        $bordered={bordered}
        $isDrawer={isDrawer}
        $newLookField={isNewLookField}
      >
        <InputWrapper
          $isLeftInput={true}
          $bordered={bordered}
          $newLookField={isNewLookField}
        >
          {icon}
          <Text {...(amountFocused ? focusedProps : unfocusedProps)}>
            <CustomInputNumber
              bordered={false}
              name={"amount"}
              value={amountInputData.value}
              onChange={amountInputData.onChange}
              precision={amountInputData.precision}
              onFocus={amountInputData.onFocus}
              onBlur={amountInputData.onBlur}
              stringMode={true}
              placeholder={amountInputData.placeholder}
              focusOnMount={focusOnMount}
              autoSize={!bordered}
              color="inherit"
            />
          </Text>
        </InputWrapper>
        <InputWrapper $newLookField={isNewLookField}>
          <Text
            {...(amountFocused ? unfocusedProps : focusedProps)}
            style={{ display: "flex", alignItems: "center" }}
          >
            <CustomInputNumber
              bordered={false}
              name={"percent"}
              value={percentInputData.value}
              onChange={percentInputData.onChange}
              precision={percentInputData.precision}
              onFocus={percentInputData.onFocus}
              onBlur={percentInputData.onBlur}
              placeholder={percentInputData.placeholder}
              stringMode={true}
              autoSize={!bordered}
              style={{
                textAlign: bordered ? "right" : "center",
                paddingLeft: 0,
                paddingRight: 0,
              }}
              color="inherit"
            />
            {isNewLookField ? <PercentSign>%</PercentSign> : "%"}
          </Text>
        </InputWrapper>
      </InputsWrapper>
      {validationMessage && (
        <ValidationMessageWrapper>{validationMessage}</ValidationMessageWrapper>
      )}
      {showSlider && (
        <div
          style={{
            width: "100%",
            height: 34,
            padding: bordered ? "0 8px" : "0 30px",
          }}
        >
          <StyledSlider
            value={parseFloat(percentInputData.value || "0")}
            onChange={(value: number) =>
              percentInputData.onChange(value.toString())
            }
            showButtons={true}
            tooltip={{ open: false }}
            maxValue={maxPercent}
          />
        </div>
      )}
    </div>
  );
};

export default ConnectedAmountPercentInputs;

const InputsWrapper = styled.div<{
  $bordered?: boolean;
  $isDrawer?: boolean;
  $newLookField?: boolean;
}>`
  display: flex;
  justify-content: ${(p) => (p.$bordered ? "space-between" : "center")};
  align-items: ${(p) => (p.$bordered ? "center" : "baseline")};
  background: ${(p) =>
    p.$isDrawer ? p.theme.colors.layoutBgColor : p.theme.colors.cardBgColor};
  ${(p) =>
    p.$bordered &&
    `
    border: ${p.theme.borders.light};
    padding: 0px 8px;
    border-radius: 4px;
  `}
  input {
    background-color: ${(p) =>
      p.$isDrawer
        ? p.theme.colors.layoutBgColor
        : p.theme.colors.cardBgColor} !important;
  }
  ${(p) =>
    p.$newLookField &&
    css`
      height: 40px;
      padding: 0 0 0 12px;
      border: 1px solid ${p.theme.colors.lightBorderGrey};
      border-radius: ${p.theme.radii.control}px;
      ${newLookFieldStates};
      input {
        font-size: 15px;
        font-weight: 500;
        font-variant-numeric: tabular-nums;
        color: ${p.theme.colors.primaryText};
        &::placeholder {
          color: ${p.theme.colors.lightestText};
        }
      }
    `}
`;
const InputWrapper = styled.div<{
  $bordered?: boolean;
  $isLeftInput?: boolean;
  $newLookField?: boolean;
}>`
  display: flex;
  align-items: ${(p) => (p.$bordered ? "center" : "baseline")};
  ${(p) =>
    p.$isLeftInput
      ? `
    padding: 4px 0;
    ${p.$bordered && `border-right: ${p.theme.borders.light};`}
  `
      : ``}
  ${(p) =>
    p.$newLookField &&
    (p.$isLeftInput
      ? css`
          flex: 1;
          min-width: 0;
          gap: 6px;
          padding: 0;
          border-right: none;
          > span {
            flex: 1;
            min-width: 0;
          }
        `
      : css`
          flex: none;
          width: 112px;
          height: 22px;
          padding: 0 12px 0 10px;
          border-left: 1px solid ${p.theme.colors.lightBorderGrey};
          > span {
            width: 100%;
          }
        `)}
`;
const PercentSign = styled.span`
  margin-left: 2px;
  font-size: 13px;
  color: ${(p) => p.theme.colors.secondaryText};
`;
const ValidationMessageWrapper = styled.div`
  margin-top: 6px;
  ${(p) =>
    p.theme.isNewLook &&
    `
    font-variant-numeric: tabular-nums;
  `}
`;
const StyledSlider = styled(Slider)`
  &.ant-slider {
    height: auto;
  }
`;
